import { useState } from 'react';
import type { PoolOfferListItem } from '../../../../modules/api';

/**
 * Aspect (w/h) of each pack art and of the widest one in `offers` — from the backend's image
 * metadata when it sends one, else learnt from the slider's own `<img>`s as
 * they load (no extra downloads). `null` until one is known. The widest one
 * so a peek never closes in past a wider neighbour.
 *
 * The metadata matters: learnt only on load, a lazily mounted neighbour's art
 * changed the slot step mid-swipe and the slider snapped back to the pack the
 * swipe started on (Betr, 2026-10-23).
 */
export function useWidestPackArt(offers: PoolOfferListItem[]) {
  const [aspects, setAspects] = useState<Readonly<Record<string, number>>>({});

  const onPackArtLoad = (offer: PoolOfferListItem, img: HTMLImageElement) => {
    const url = offer.packImageUrl;
    if (!url || img.naturalWidth <= 0 || img.naturalHeight <= 0) return;
    const aspect = img.naturalWidth / img.naturalHeight;
    setAspects((prev) => (prev[url] === aspect ? prev : { ...prev, [url]: aspect }));
  };

  const artAspectOf = (offer: PoolOfferListItem): number | null => {
    const loaded = offer.packImageUrl ? aspects[offer.packImageUrl] : undefined;
    return offer.packImageAspect ?? loaded ?? null;
  };

  const known = offers.flatMap((o) => {
    const aspect = artAspectOf(o);
    return aspect === null ? [] : [aspect];
  });
  const widestArtAspect = known.length > 0 ? Math.max(...known) : null;

  return { widestArtAspect, artAspectOf, onPackArtLoad };
}
